import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";

// Pages
import HomePage from "./pages/HomePage";
import AddressPage from "./pages/AddressPage";
import RoutePage from "./pages/RoutePage";
import AdminLogin from "./pages/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminAddresses from "./pages/admin/AdminAddresses";
import AdminTransactions from "./pages/admin/AdminTransactions";
import AdminRoutes from "./pages/admin/AdminRoutes";
import AdminWatchlist from "./pages/admin/AdminWatchlist";
import AdminArchives from "./pages/admin/AdminArchives";
import AdminArchiveView from "./pages/admin/AdminArchiveView";

import AdminAddressEdit from "./pages/admin/AdminAddressEdit";

// Always use current origin to avoid cross-origin issues
export const API = `${window.location.origin}/api`;

// Protected route component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Skip if user data passed from login
    if (location.state?.user) {
      setUser(location.state.user);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/admin/login", { replace: true });
      }
    };

    checkAuth();
  }, [location, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return children({ user });
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<HomePage />} />
          <Route path="/address/:addressId" element={<AddressPage />} />
          <Route path="/route/:routeId" element={<RoutePage />} />
          
          {/* Admin routes */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={
            <ProtectedRoute>
              {({ user }) => <AdminDashboard user={user} />}
            </ProtectedRoute>
          } />
          <Route path="/admin/addresses" element={
            <ProtectedRoute>
              {({ user }) => <AdminAddresses user={user} />}
            </ProtectedRoute>
          } />
          <Route path="/admin/addresses/:addressId/edit" element={
            <ProtectedRoute>
              {({ user }) => <AdminAddressEdit user={user} />}
            </ProtectedRoute>
          } />
          <Route path="/admin/transactions" element={
            <ProtectedRoute>
              {({ user }) => <AdminTransactions user={user} />}
            </ProtectedRoute>
          } />
          <Route path="/admin/routes" element={
            <ProtectedRoute>
              {({ user }) => <AdminRoutes user={user} />}
            </ProtectedRoute>
          } />
          <Route path="/admin/watchlist" element={
            <ProtectedRoute>
              {({ user }) => <AdminWatchlist user={user} />}
            </ProtectedRoute>
          } />
          <Route path="/admin/archives" element={
            <ProtectedRoute>
              {({ user }) => <AdminArchives user={user} />}
            </ProtectedRoute>
          } />
          <Route path="/admin/archives/:archiveName" element={
            <ProtectedRoute>
              {({ user }) => <AdminArchiveView user={user} />}
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
